import { useToast } from "@chakra-ui/react";

// Track generated toast IDs to manage related toasts
const toastTracker = new Map<string, string>();

// Cleanup old entries to prevent memory leaks (keep only last 100 entries)
const cleanupToastTracker = () => {
	if (toastTracker.size > 100) {
		// Convert to array, sort by most recently added
		const entries = Array.from(toastTracker.entries());
		// Delete oldest entries (first 20%)
		const entriesToDelete = Math.floor(entries.length * 0.2);
		for (let i = 0; i < entriesToDelete; i++) {
			toastTracker.delete(entries[i][0]);
		}
	}
};

/**
 * A simple Claims hook wrapper for Chakra's useToast hook for brevity and standardization
 */
export const useClaimsToast = () => {
	const chakraToast = useToast();

	/**
	 * Internal function to call Chakra's useToast hook, similar parameters are required
	 * @param title The title of the problem
	 * @param description A string describing the problem
	 * @param status typeof: "info" | "warning" | "success" | "error" | "loading" | undefined
	 * @param duration Milliseconds to show the toast
	 * @param id Optional ID to update an existing toast instead of creating a new one
	 * @param onCloseComplete A function to call when the toast is closed
	 */
	const toast = (
		title: string | undefined,
		description: string | undefined,
		status: "error" | "loading" | "success" | "info" | "warning" | undefined,
		duration?: number,
		id?: string,
		onCloseComplete?: () => void
	) => {
		// Generate a tracking key based on the original ID (or create a new one)
		const baseId = id || `toast-${status || "default"}`;

		// If we're trying to update an existing toast
		if (id) {
			// Check if we have a tracked unique ID for the passed id
			const trackedId = toastTracker.get(baseId);

			// If we have a tracked ID and it's active, close it
			if (trackedId && chakraToast.isActive(trackedId)) {
				chakraToast.close(trackedId);
			}
			// If base ID itself is active, close it too (backward compatibility)
			else if (chakraToast.isActive(baseId)) {
				chakraToast.close(baseId);
			}
		}

		// Generate a globally unique ID
		const uniqueId = `${baseId}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

		// Store the unique ID with the original ID as the key
		toastTracker.set(baseId, uniqueId);

		// Run cleanup to prevent memory leaks
		cleanupToastTracker();

		// If it's a short duration, treat it as a closing toast
		if (duration && duration <= 1) {
			// Only close the toast without showing a new one
			const trackedId = toastTracker.get(baseId);
			if (trackedId && chakraToast.isActive(trackedId)) {
				chakraToast.close(trackedId);
			}
			return uniqueId;
		}

		// Create the toast with a unique ID
		chakraToast({
			id: uniqueId,
			title: title,
			description: description,
			status: status,
			duration: duration ?? 9000,
			isClosable: true,
			position: "top",
			onCloseComplete: onCloseComplete
		});

		// Return the unique ID for future reference
		return uniqueId;
	};

	return toast;
};

export default useClaimsToast;
